const BiopharmaceuticsUnit5Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit V: Nonlinear Pharmacokinetics</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            In previous units, we assumed that pharmacokinetic processes (ADME) follow <strong>First-Order (Linear) kinetics</strong>, where the rate of the process is directly proportional to the drug concentration. However, when specific carrier systems or enzymes become saturated at high doses, the kinetics change. This is known as <strong>Nonlinear or Dose-Dependent Pharmacokinetics</strong>.
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Introduction to Non-linearity</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="bg-blue-50 p-4 border border-blue-100 rounded-lg">
                <h4 class="font-bold text-blue-900 text-[14px] mb-2">Linear Pharmacokinetics</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-1">
                    <li>Follows <strong>First-Order</strong> kinetics at all dose levels.</li>
                    <li>Pharmacokinetic parameters (t<sub>1/2</sub>, V<sub>d</sub>, Clearance, K<sub>E</sub>) remain <strong>constant</strong> regardless of the dose.</li>
                    <li>AUC is directly proportional to the dose.</li>
                    <li><em>Example:</em> Most drugs at standard therapeutic doses.</li>
                </ul>
            </div>
            
            <div class="bg-red-50 p-4 border border-red-100 rounded-lg">
                <h4 class="font-bold text-red-900 text-[14px] mb-2">Nonlinear Pharmacokinetics</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-1">
                    <li>Kinetics change from First-Order to <strong>Zero-Order</strong> or mixed-order at higher doses due to saturation.</li>
                    <li>Parameters like t<sub>1/2</sub> and Clearance <strong>change</strong> with the dose.</li>
                    <li>AUC increases disproportionately with an increase in dose.</li>
                    <li><em>Also called:</em> Capacity-limited, dose-dependent, or saturation kinetics.</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">2. Factors Causing Non-linearity</h3>
        <p class="text-[13px] text-slate-700 mb-4">Non-linearity can occur at any stage of the ADME process when a capacity-limited system becomes saturated.</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-blue-800 text-[14px] mb-1">A. In Drug Absorption</h4>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Saturation of carrier proteins:</strong> E.g., Riboflavin (Vitamin B2) absorption decreases at high doses because active transport carriers get saturated.</li>
                    <li><strong>Low aqueous solubility:</strong> E.g., Griseofulvin. At high doses, not all of the drug dissolves in GI fluids.</li>
                </ul>
            </div>
            
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-blue-800 text-[14px] mb-1">B. In Drug Distribution</h4>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Saturation of Plasma Proteins:</strong> At high doses, binding sites on Albumin become fully occupied. This leads to a sudden increase in the <em>free fraction</em> of the drug.</li>
                    <li><em>Example:</em> Phenylbutazone, Salicylates. V<sub>d</sub> increases as the dose increases.</li>
                </ul>
            </div>

            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-blue-800 text-[14px] mb-1">C. In Drug Metabolism</h4>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Saturation of Hepatic Enzymes (CYP450):</strong> The most common cause of non-linearity. When enzymes are saturated, metabolism shifts from first-order to zero-order.</li>
                    <li><em>Examples:</em> <strong>Phenytoin, Alcohol, Theophylline.</strong> A small increase in dose causes a massive toxic jump in plasma concentration.</li>
                </ul>
            </div>

            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-blue-800 text-[14px] mb-1">D. In Drug Excretion</h4>
                <ul class="list-disc pl-4 space-y-1 text-[12px] text-slate-600">
                    <li><strong>Saturation of Active Tubular Secretion:</strong> Carriers in the kidney become saturated, decreasing renal clearance. (e.g., Penicillin G).</li>
                    <li><strong>Saturation of Tubular Reabsorption:</strong> Carriers for reabsorption get saturated, increasing excretion. (e.g., Ascorbic acid / Vitamin C).</li>
                </ul>
            </div>
        </div>
    </div>

    <div class="bg-indigo-50 p-6 border border-indigo-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-900 mb-3 border-b border-indigo-200 pb-2">3. Michaelis-Menten Equation</h3>
        <p class="text-[13px] text-slate-700 mb-4 leading-relaxed">
            The kinetics of capacity-limited processes (like enzyme metabolism or carrier-mediated transport) are best described by the <strong>Michaelis-Menten Equation</strong>.
        </p>

        <div class="bg-white p-4 rounded border border-indigo-200 text-center mb-5 shadow-sm">
            <p class="text-[13px] font-bold text-indigo-900 mb-2">Rate of Elimination (-dC/dt)</p>
            <div class="font-mono text-[16px] font-bold text-blue-800 bg-slate-50 py-2 rounded inline-block px-6 border border-slate-200">
                -dC/dt = (V<sub>max</sub> × C) / (K<sub>m</sub> + C)
            </div>
            <div class="mt-3 text-left">
                <ul class="list-disc pl-8 text-[12px] text-slate-600 space-y-1">
                    <li><strong>C:</strong> Plasma concentration of the drug.</li>
                    <li><strong>V<sub>max</sub>:</strong> Maximum elimination rate (when all enzymes are 100% saturated).</li>
                    <li><strong>K<sub>m</sub>:</strong> Michaelis Constant. It is the plasma concentration at which the elimination rate is exactly half of V<sub>max</sub>. It reflects the affinity of the enzyme for the drug.</li>
                </ul>
            </div>
        </div>

        <h4 class="text-[15px] font-bold text-indigo-900 mb-3">Understanding the Equation at Different Doses:</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="bg-white p-4 rounded-lg border border-slate-200">
                <h5 class="font-bold text-green-700 text-[13px] mb-1">Condition 1: Low Dose (C &lt;&lt; K<sub>m</sub>)</h5>
                <p class="text-[12px] text-slate-600 mb-2">When drug concentration is very low, C in the denominator is negligible compared to K<sub>m</sub>. The equation becomes:</p>
                <p class="font-mono text-[11px] font-bold text-center bg-slate-100 p-1 mb-2">-dC/dt = (V<sub>max</sub> / K<sub>m</sub>) × C</p>
                <p class="text-[12px] text-slate-700 font-medium text-center text-green-800">Result: Follows FIRST-ORDER Kinetics.</p>
            </div>
            
            <div class="bg-white p-4 rounded-lg border border-slate-200">
                <h5 class="font-bold text-red-700 text-[13px] mb-1">Condition 2: High Dose (C &gt;&gt; K<sub>m</sub>)</h5>
                <p class="text-[12px] text-slate-600 mb-2">When drug concentration is very high, K<sub>m</sub> in the denominator becomes negligible compared to C. The C's cancel out:</p>
                <p class="font-mono text-[11px] font-bold text-center bg-slate-100 p-1 mb-2">-dC/dt = V<sub>max</sub></p>
                <p class="text-[12px] text-slate-700 font-medium text-center text-red-800">Result: Follows ZERO-ORDER Kinetics.</p>
            </div>
        </div>
    </div>

    <div class="bg-white p-5 mt-6 border border-slate-200 rounded-xl shadow-sm">
        <h4 class="text-[15px] font-bold text-blue-900 mb-2">Estimating V<sub>max</sub> and K<sub>m</sub></h4>
        <p class="text-[13px] text-slate-700 mb-2">To calculate these parameters clinically (e.g., for Phenytoin dosing), we rearrange the Michaelis-Menten equation into linear forms using graphical methods:</p>
        <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
            <li><strong>Lineweaver-Burk Plot:</strong> (Double Reciprocal Plot) Plotting 1/v versus 1/C.</li>
            <li><strong>Eadie-Hofstee Plot:</strong> Plotting v versus v/C.</li>
            <li><strong>Direct Linear Plot.</strong></li>
        </ul>
    </div>

</div>
`;

export default BiopharmaceuticsUnit5Notes;